import api from "@/lib/api";

/** Feature-detect Web Push. */
export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

/** iOS 16.4+ requires the PWA to be added to the home screen for Web Push. */
export function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = typeof navigator !== "undefined" && "standalone" in navigator && navigator.standalone;
  return Boolean(mq || iosStandalone);
}

/** iOS Safari user agent detection (for permission-flow copy). */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  // If we already have an active worker, reuse it. Otherwise register /sw.js.
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await getRegistration();
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

function subscriptionToBody(sub) {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    keys: json.keys,
    expirationTime: json.expirationTime,
    user_agent: navigator.userAgent,
    platform: /Android/.test(navigator.userAgent) ? "android"
      : isIOS() ? "ios"
      : "desktop",
  };
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error("Push notifications aren't supported by this browser.");
  if (isIOS() && !isStandaloneDisplay()) {
    throw new Error("On iPhone, tap Share → Add to Home Screen, then open AddVendTour from the home-screen icon before enabling notifications.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notifications were blocked. You can re-enable them in your browser settings.");
  }
  const { data } = await api.get("/push/vapid-public-key");
  const vapidKey = data.public_key;
  const reg = await getRegistration();
  // Reuse any pre-existing subscription — otherwise create one.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }
  await api.post("/push/subscribe", subscriptionToBody(sub));
  return sub;
}

export async function unsubscribeFromPush() {
  const sub = await getCurrentSubscription();
  if (!sub) return { ok: true, endpoint: null };
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* ignore */ }
  return { ok: true, endpoint };
}
